import { useEffect, useMemo } from 'react';
import { useApp, type Panel } from '../store';
import { panelForShortcutAction, type ShortcutAction } from '../lib/keyboardShortcuts';

/** True si le focus est dans un champ où les raccourcis ne doivent pas intervenir. */
export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (target.isContentEditable) return true;
  return !!target.closest('[contenteditable="true"], input, textarea, select');
}

const PANEL_ACTIONS: ShortcutAction[] = [
  'resources',
  'technologies',
  'buildings',
  'empire',
  'alliances',
  'onu',
  'leaderboard',
];

/**
 * Raccourcis globaux (touches configurables dans Profil → Raccourcis).
 * L’invest est géré dans TerritoryPanel.
 */
export function useGameKeyboardShortcuts() {
  const { user, panel, togglePanel, setPanel, interfaceHidden, tutorialOpen, newsOpen, shortcuts } =
    useApp();

  const panelByKey = useMemo(() => {
    const map: Record<string, Exclude<Panel, 'none'>> = {};
    for (const action of PANEL_ACTIONS) {
      const p = panelForShortcutAction(action);
      if (p) map[shortcuts[action]] = p;
    }
    return map;
  }, [shortcuts]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.repeat) return;
      if (isTypingTarget(e.target)) return;
      if (interfaceHidden || tutorialOpen || newsOpen) return;
      if (panel === 'shortcuts') return;

      const key = e.key.length === 1 ? e.key.toLowerCase() : e.key;
      const next = panelByKey[key];
      if (!next) return;

      // Classement accessible hors connexion ; le reste exige un compte.
      if (next !== 'leaderboard' && !user) {
        setPanel(panel === 'auth' ? 'none' : 'auth');
        e.preventDefault();
        return;
      }
      if (user?.needsEmpireName && next !== 'leaderboard') {
        setPanel('profile');
        e.preventDefault();
        return;
      }

      togglePanel(next);
      e.preventDefault();
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [user, panel, togglePanel, setPanel, interfaceHidden, tutorialOpen, newsOpen, panelByKey]);
}
